import React, { useCallback, useMemo, useState } from 'react';
import ConnectedFeaturedCaseGallery from '@/components/FeaturedCaseGallery/Connected';
import useFeaturedCaseActions from '@/components/FeaturedCaseGallery/useActions';
import { replaceTemplateWithElements, useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { mtstatTrack } from '$/utils/mtstat';
import styles from './index.module.scss';

/** 语言和区域变化后重新加载，不能沿用上一个投放上下文的列表。 */
export default function HomeFeaturedCases() {
  const locale = useLanguageStore((state) => state.languageStore.languageCode);
  const country = useOfficialStore((state) => state.currentCountryCode);
  const overseas = useOfficialStore((state) => state.isOverseaServer);
  return <HomeFeaturedContent key={JSON.stringify([locale, country, overseas])} />;
}

/** 首页卡位从 0 开始并按文档转为字符串；业务身份只取接口提供的值。 */
const caseParams = (item: Parameters<ReturnType<typeof useFeaturedCaseActions>['openCase']>[0], position: number) => ({
  position: String(position),
  case_id: item.caseId || '',
  room_id: item.roomId || '',
  project_id: item.projectId || '',
  ip_id: (item.castIps || []).map((ip) => ip.id).join(','),
});

/** 本期首页不展示分类，也不请求分类接口；官网投放位默认读取全部精选。 */
function HomeFeaturedContent() {
  const [reloadKey, setReloadKey] = useState(0);
  const translate = useLanguageStore((state) => state.languageStore.smartcatTranslationText);
  const {
    openCase, createSame, creatingFeaturedId, onLoaded,
  } = useFeaturedCaseActions();
  const labels = useMemo(() => ({
    createSame: translate((value) => value.view_creation_process),
    /** 服务端 casting_ip_count 为总数，不能使用截断后的数组长度。 */
    formatCastIpCount: (count: number) => replaceTemplateWithElements('count_IPs_participate', { count: String(count) }),
  }), [translate]);
  /** 由画廊的可见区观察器触发曝光。 */
  const handleCaseExposed = useCallback((item: Parameters<typeof openCase>[0], index: number) => {
    mtstatTrack('roboneo_homecase_exp', caseParams(item, index));
  }, []);
  /** 点击卡片后携带真实案例身份进入详情。 */
  const handleCaseClick = useCallback((item: Parameters<typeof openCase>[0], index: number) => {
    mtstatTrack('roboneo_homecase_click', caseParams(item, index));
    openCase(item);
  }, [openCase]);
  /** 查看创作流程独立上报文档约定的四个字段，不混入曝光事件专用参数。 */
  const handleCreateSame = (item: Parameters<typeof openCase>[0], index: number) => {
    const { ip_id, case_id, position } = caseParams(item, index);
    mtstatTrack('roboneo_case_ciew_cretative_process_click', {
      ip_id, case_id, position, location: 'home_page',
    });
    return createSame(item);
  };

  /** 用户主动重试时重新挂载请求容器，不触发登录或其他业务操作。 */
  const retry = () => setReloadKey((value) => value + 1);

  return (
    <section aria-labelledby="home-featured-cases-title">
      <h2 id="home-featured-cases-title" className={styles.title}>{translate((value) => value.curated_content)}</h2>
      <ConnectedFeaturedCaseGallery
        key={reloadKey}
        title={null}
        embedded
        requestParams={{ placement: 'website_home', limit: 20 }}
        onLoaded={onLoaded}
        labels={labels}
        creatingFeaturedId={creatingFeaturedId}
        onCaseClick={handleCaseClick}
        onCaseExposed={handleCaseExposed}
        onCreateSameClick={handleCreateSame}
        loadingFallback={<div className={styles.status} role="status">{translate((value) => value.RoboNeo_Web_loading)}</div>}
        emptyFallback={<div className={styles.status} role="status">{translate((value) => value.no_content)}</div>}
        errorFallback={(
          <div className={styles.status} role="alert">
            <span>{translate((value) => value.page_load_failed_retry_later)}</span>
            <button type="button" className={styles.retry} onClick={retry}>{translate((value) => value.roboneo_web_retry)}</button>
          </div>
        )}
      />
    </section>
  );
}
